import React, { Fragment, useState } from 'react'
import { shape, bool, string, func, arrayOf } from 'prop-types'
import { renderTextInput } from '../TextInput/TextInput'
import { RenderSelect } from '../Select/Select'

import './CoPassengerConfig.scss'

function validateCoPassenger(coPassenger) {
  return (
    coPassenger.passengerType &&
    coPassenger.firstName &&
    coPassenger.lastName &&
    !isNaN(new Date(coPassenger.dateOfBirth).getTime()) &&
    coPassenger.gender
  )
}

const CoPassengerRow = ({
  labels,
  fields,
  formRowClasses,
  index,
  coPassenger,
  onChange,
  onDelete,
  renderErrors
}) => {
  const handleUpdate = (key, value) =>
    onChange({ ...coPassenger, [key]: value })
  const validPassengerTypes = ['A', 'S', 'C', 'I'] // NOTE(ebarrett): This is MAI specific
  const passengerTypeSelect = RenderSelect({
    name: 'passengerType',
    value: coPassenger.passengerType,
    setValue: handleUpdate,
    options: fields.passengerType.options.filter(opt =>
      validPassengerTypes.includes(opt.value)
    ),
    label: labels.passengerType,
    forceFloatingLabel: true
  })

  const genderSelect = RenderSelect({
    name: 'gender',
    value: coPassenger.gender,
    setValue: handleUpdate,
    options: fields.gender.options,
    label: labels.gender,
    forceFloatingLabel: true
  })

  const makeInputFor = (key, formatValue = v => v) => {
    return renderTextInput({
      name: key,
      value: formatValue(coPassenger[key]),
      type: 'text',
      placeholder: labels[key],
      setFieldValue: handleUpdate,
      forceFloatingLabel: true
    })
  }

  const isValid = validateCoPassenger(coPassenger)

  return (
    <Fragment>
      <span className="row">
        <div className="copassenger-config-row upper">
          <div className="copassenger-config-label index">
            {index + 1 + '.'}
          </div>
          <div className="copassenger-config-input passenger-type">
            {passengerTypeSelect}
          </div>
          <div className="copassenger-config-input">
            {makeInputFor('firstName')}
          </div>
          <div className="copassenger-config-input">
            {makeInputFor('lastName')}
          </div>
          <div className="copassenger-config-label delete" onClick={onDelete}>
            X
          </div>
        </div>
      </span>
      <span className="row">
        <div className="copassenger-config-row lower">
          <div className="copassenger-config-input birth-date">
            {makeInputFor('dateOfBirth')}
          </div>
          <div className="copassenger-config-input gender">{genderSelect}</div>
        </div>
      </span>
      {!isValid && renderErrors && (
        <span className={formRowClasses + ' copassenger-config-error'}>
          {labels.coPassengerError}
        </span>
      )}
    </Fragment>
  )
}

CoPassengerRow.propTypes = {
  formRowClasses: string.isRequired,
  coPassenger: shape({}).isRequired,
  onChange: func.isRequired,
  onDelete: func.isRequired,
  renderErrors: bool
}

const CoPassengerConfig = ({
  labels,
  fields,
  formRowClasses,
  initialValues,
  onChange,
  renderErrors
}) => {
  const [coPassengers, setCoPassengers] = useState(
    initialValues.map(x => {
      return { ...x }
    })
  )

  const addCoPassenger = evt => {
    const newCoPassenger = {
      passengerType: 'A',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      coPassengerId: ''
    }
    setCoPassengers([...coPassengers, newCoPassenger])
    onChange([...coPassengers, newCoPassenger], false)
    evt.preventDefault() // Don't show the validation labels on outer form
  }

  const removeCoPassenger = index => {
    const copy = [...coPassengers]
    copy.splice(index, 1)
    setCoPassengers(copy)
    onChange(copy, validateCoPassengers(copy))
  }

  const updateCoPassenger = (coPassenger, index) => {
    const copy = [...coPassengers]
    copy[index] = coPassenger
    setCoPassengers(copy)
    onChange(copy, validateCoPassengers(copy))
  }

  const validateCoPassengers = data => {
    return data.every(coPassenger => validateCoPassenger(coPassenger))
  }

  return (
    <div className="copassenger-config">
      {coPassengers.map((cp, index) => (
        <Fragment key={`cp${index}`}>
          <CoPassengerRow
            formRowClasses={formRowClasses}
            labels={labels}
            fields={fields}
            coPassenger={cp}
            index={index}
            renderErrors={renderErrors}
            onChange={cp => updateCoPassenger(cp, index)}
            onDelete={cp => removeCoPassenger(index)}
          />
          {index < coPassengers.length - 1 && (
            <div className="copassenger-config-hr" />
          )}
        </Fragment>
      ))}
      <div className="copassenger-config-btn-container">
        <button className="btn btn-primary" onClick={addCoPassenger}>
          Add Co-Passenger
        </button>
      </div>
    </div>
  )
}

CoPassengerConfig.propTypes = {
  labels: shape().isRequired,
  fields: shape().isRequired,
  initialValues: arrayOf(shape()).isRequired,
  onChange: func.isRequired, // function(newValue, isValid)
  formRowClasses: string.isRequired,
  renderErrors: bool
}

export default CoPassengerConfig

/*

  NOTE(ebarrett): SingleDatePicker doesn't have support for jumping to a specific year,
    so while I was able to get the date picker up with the snippets below, picking
    a dateOfBirth would be extremely tedious having to scroll backwards one month at a time

  ...

  import { SingleDatePicker } from 'react-dates'
  import FerryRoutesFormDateSelect from '../ferryRoutes/elements/dates/FerryRoutesFormDateSelect'

  ...

  const dobPickerId = `copassenger-dob-${index}`
  const toggleCustomDate = dateField => {
    document.getElementById(dobPickerId).focus()
    setFocus(true)
  }
  const dateOfBirthInput = <div style={{minWidth:'230px'}} onClick={toggleCustomDate}>
    <div style={{display:focus ? 'inherit' : 'none', height: 0, zIndex:100}}>
      <SingleDatePicker
        block={false}
        date={moment(coPassenger.dateOfBirth)}
        daySize={36}
        displayFormat="MMM D, YYYY"
        focused={focus}
        horizontalMargin={30}
        id={dobPickerId}
        isOutsideRange={date =>
          date.isAfter(moment())
        }
        navNext={<i className="material-icons">east</i>}
        navPrev={<i className="material-icons">west</i>}
        noBorder={true}
        numberOfMonths={1}
        onDateChange={e => console.log("asdf")}
        onFocusChange={() => setFocus(!focus)}
        firstDayOfWeek={0}
      />
    </div>
    {makeInputFor('dateOfBirth', v => v.split('T')[0])}
  </div>
  
*/
